import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import SwaggerPage from "../components/swaggerPage/swaggerPage";
import { getUserIdFromState } from "../mappers/backendMappers";
import APIProductService from "../services/APIProductService";
import * as actions from "../actions";

const normalizeAlias = (value) => {
  if (!value) return null;

  return String(value)
    .replace(/^\/apiProduct\//, "")
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
};

const findProductByAlias = (products, identifier) => {
  const target = normalizeAlias(identifier);
  if (!target || !Array.isArray(products)) return null;

  return (
    products.find((product) => {
      const candidates = [product?.alias, product?.urlAlias, product?.UrlAlias]
        .filter(Boolean)
        .map(normalizeAlias);

      return candidates.includes(target);
    }) || null
  );
};

const SwaggerPageHOC = (props) => {
  // Extract product data from props or route params
  const { match, location } = props;
  const { apiProductId, apiName } = match?.params || {};

  // State for product data and swagger content
  const [productData, setProductData] = useState(null);
  const [swaggerData, setSwaggerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiEndpoint, setApiEndpoint] = useState(null);

  // Determine which route pattern was used
  const isAlternateRoute = !apiProductId && apiName;

  // Fetch product data when component mounts using APIProductService
  useEffect(() => {
    const fetchProductData = async () => {
      if (!apiProductId || !apiName) {
        setError("Missing product ID or API name");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const userId = props.userId || "00000000-0000-0000-0000-000000000000";
        const csrfToken = props.csrfToken || "";
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        let resolvedProductId = apiProductId;

        // If route contains alias instead of UUID, resolve it first
        if (!uuidRegex.test(apiProductId)) {
          let matchingProduct = findProductByAlias(
            APIProductService.getStoredProductList() || [],
            apiProductId
          );

          if (!matchingProduct) {
            await APIProductService.getProductList(userId, csrfToken);
            matchingProduct = findProductByAlias(
              APIProductService.getStoredProductList() || [],
              apiProductId
            );
          }

          if (!matchingProduct) {
            setError("Product not found");
            setLoading(false);
            return;
          }

          resolvedProductId =
            matchingProduct.uuid || matchingProduct.id || matchingProduct.Id;
        }

        const productResponse = await APIProductService.getProductDocument(
          resolvedProductId,
          userId,
          csrfToken
        );

        if (productResponse && productResponse.original) {
          const product = productResponse.original;
          setProductData(product);

          // Use APIProductService to find the specific API endpoint by name
          const targetApi = APIProductService.findAPI(apiName);

          if (targetApi) {
            setApiEndpoint(targetApi);

            // Use APIProductService to get decoded swagger data
            const swaggerJson = APIProductService.getSwagger();

            if (swaggerJson) {
              setSwaggerData(swaggerJson);
            } else {
              setError("No swagger documentation available for this API");
            }
          } else {
            setError("API endpoint not found");
          }
        } else {
          setError("Invalid product data received");
        }
      } catch (err) {
        console.error("Error fetching product data:", err);
        setError(
          "Failed to load product data: " + (err.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [apiProductId, apiName, props.userId, props.csrfToken]);

  // Cleanup APIProductService when component unmounts
  useEffect(() => {
    return () => {
      APIProductService.clear();
    };
  }, []);

  // Prepare props for SwaggerPage component
  const swaggerPageProps = {
    uuid: productData?.id || productData?.Id || apiProductId || "",
    apiName: apiName,
    title: productData?.productName || "API Documentation",
    apiTitle: apiEndpoint?.apiName || "API Documentation",
    breadcrumb: {
      path: [
        { name: "Home", href: "/" },
        { name: "API Products", href: "/apiProducts" },
        ...(apiProductId && productData
          ? [
              {
                name: productData.productName,
                href: `/apiProduct/${
                  productData?.urlAlias || productData?.UrlAlias || apiProductId
                }`,
              },
            ]
          : []),
        { name: apiEndpoint?.apiName || "Swagger Documentation" },
      ],
    },
    isAlternateRoute,
    swaggerData,
    apiEndpoint,
    productData,
    loading,
    error,
    ...props,
  };

  return <SwaggerPage {...swaggerPageProps} />;
};

const mapStateToProps = (state) => ({
  isLoggedIn: state.applicationUser?.isLoggedIn,
  applicationUser: state.applicationUser,
  userId: getUserIdFromState(state),
  csrfToken: state.applicationUser?.token,
  applications: state.applicationUser?.applications,
  tryouts: state.tryout,
});

const mapDispatchToProps = {
  createTryout: actions.createTryout,
  showTryout: actions.showTryout,
  updateTryout: actions.updateTryout,
  tryoutRequestCompleted: actions.tryoutRequestCompleted,
  clearAuthCode: actions.clearAuthCode,
  setAuthCode: actions.setAuthCode,
  getAccessToken: actions.getAccessToken,
  setAccessToken: actions.setAccessToken,
  tryoutResponseClear: actions.tryoutResponseClear,
  clearTryoutHistory: actions.clearTryoutHistory,
  favoriteTryoutHistoryItem: actions.favoriteTryoutHistoryItem,
  deleteTryoutHistoryItem: actions.deleteTryoutHistoryItem,
  importTryoutHistory: actions.importTryoutHistory,
  editTryoutDescription: actions.editTryoutDescription,
};

export default connect(mapStateToProps, mapDispatchToProps)(SwaggerPageHOC);
